import React from 'react';
import { Container, Row, Col } from 'reactstrap';
import VASTPlayer from 'vast-player';

import DataStore from '../base/plumbing/DataStore';
import VideoObject from '../data/VideoObject';

import { Card } from '../base/components/CardAccordion';
import Misc from '../base/components/Misc';
import PropControl from '../base/components/PropControl';

import { KAdFormat } from '../base/data/Advert';
import { encURI } from '../base/utils/miscutils';
import Dt, { TUnit } from '../base/data/Dt';


function VastPreview({ vastTag, widgetName = "VastPreview" }) {
	if (!vastTag) {
		return <div id={widgetName} className="VastPreview" />;
	}
	const wpath = ['widget', widgetName];
	const wstate = DataStore.getValue(wpath) || DataStore.setValue(wpath, {}, false);
	// remove a vast player?
	if (wstate.player && wstate.vastTag !== vastTag) {
		// TODO remove old player
	}
	// make a vast player?
	if (!wstate.player && !wstate.loading) {
		wstate.loading = true;
		setTimeout(() => {
			wstate.player = new VASTPlayer(document.getElementById(widgetName));
			wstate.vastTag = vastTag;
			wstate.player.load(vastTag);
		}, 100);
	}
	return <div id={widgetName} className="VastPreview" />;
}


/**
 * Edit one video
 */
function EditVideoForm({ item: video, path, i: index, list }) {
	const description = [index + 1, video.mobile ? 'Mobile' : null, video.aspect].filter(a => a).join(' ');

	const onUpload = ({ response }) => {
		// Skip if not using media server
		if (!response.cargo) return;
		const { mobile, standard } = response.cargo;
		if (!mobile || !standard) return;

		// Default to 16:9 aspect, but don't overwrite previous value for this video
		const { aspect = '16:9' } = DataStore.getValue(path);

		// Replace the currently-focused video with the appropriate desktop or mobile version
		const newVid = video.mobile ? mobile : standard;
		DataStore.setValue(path, VideoObject.fromUpload({ ...newVid, aspect, mobile: video.mobile }));

		// If the focused video is for desktop, also append the mobile version to the videos list
		if (!video.mobile) {
			const mobileVideo = VideoObject.fromUpload({ ...mobile, aspect, mobile: true });
			DataStore.setValue(path.slice(0, -1), [...list, mobileVideo]);
		}
	};

	// Slightly different fields depending on video type
	const formatSpecific = video.format === 'video' ? <>
		<PropControl label="Video file or URL" prop="url" path={path} item={video} type="videoUpload"
			help="Recommended aspect ratio: 16:9 for desktop and 9:16 for mobile. Max length: 60 seconds. Max file size: 10MB."
			onUpload={onUpload}
			version={video.mobile ? "mobile" : "standard"}
		/>
		<p>
			Video files usually need to be transcoded -- which we currently do automatically (using the uploader and HandBrake-CLI).
			If you have a large file, use <a href="//wetransfer.com/" target="_blank">WeTransfer</a> to send it to Support, who can transcode it for you.
		</p>
		<PropControl label="For mobile?" path={path} item={video} prop="mobile" type="checkbox" help="" />
	</> : <>
		<PropControl label="VAST/VPAID tag url" path={path} prop="url" item={video} />
		<VastPreview vastTag={video.vastTag} />
		<PropControl label="No preload" type="checkbox" prop="nopreload" path={path.concat('advanced')}
			help="Do not attempt to preload this VAST tag - it malfunctions if loaded before the player is visible or otherwise ready."
		/>
		<PropControl label="Nested " type="checkbox" prop="nested" path={path}
			help="This VAST tag should be loaded on a *.good-loop.com page. Use when selling inventory in Good-Loop units via deals."
		/>
	</>;

	return <>
		<h4>{description}</h4>
		<div className="clearfix" />
		<Misc.Col2 noContainer>
			<PropControl label="Ad format" path={path} item={video} prop="format" type="select"
				options={['video', 'vast-vpaid']}
				labels={{ video: 'Video file', 'vast-vpaid': 'VAST/VPAID tag' }}
			/>
			<PropControl label="Video aspect ratio" path={path} prop="aspect" type="select"
				options={['', '16:9', '4:3', '1:1', '9:16', '1:2', '4:5']}
				labels={{ '': 'Do not set aspect ratio', '16:9': '16:9 (landscape)', '1:1': '1:1 (square)', '9:16': '9:16 (portrait)' }}
				help="If you&#39;re not sure, or your VAST/VPAID tag can retrieve videos with different aspect ratios, leave this blank."
			/>
		</Misc.Col2>
		<Misc.Col2 noContainer>
			{formatSpecific}
			<PropControl label="Video length" type="dt" prop="duration" path={path} item={video}
				help="How long is this video?" 
				unitOptions={["SECOND"]}
			/>
		</Misc.Col2>
	</>;
}

/**
 * Edit the set of videos
 */
function EditVideosForm({ advert, path, ...props }) {
	const isSocial = KAdFormat.social === advert.format;
	// TODO split into social vs watch-to-donate
	// Where's this form being shown and what kind of ad is it for?
	// Social: Show splash uploader only in design step 1, and main video uploader in design step 2
	// Player: Show main video uploader in design step 1, and nothing in design step 2.
	if ( ! isSocial && props.endcard) {
		return null;
	}
	const showMainVideos = (isSocial && props.endcard) || (!isSocial && props.splash);

	// ensure at least one video
	if (!advert.videos) advert.videos = [];
	if (!advert.videos.length) {
		// old data
		if (advert.video) {
			advert.videos.push({ url: advert.video });
		} else if (advert.vastTag) {
			advert.videos.push({ url: advert.vastTag });
		} else {
			// TODO make a blank entry
			advert.videos.push({});
		}
	}
	
	// Social thumbnail may be video or static image
	let title;
	let vid0 = advert.videos[0];
	if (isSocial && props.splash) {
		const thumbUrl = DataStore.getValue([...path, 'mockSocialImage']) || '';
		const Thumbnail = thumbUrl.match(/(png|jpe?g|svg)$/) ? Misc.ImgThumbnail : Misc.VideoThumbnail;
		title = <>Splash Video <Thumbnail width={75} height={50} url={thumbUrl} controls={false} /></>;
	} else {
		const thumbUrl = (vid0 && vid0.url) || DataStore.getValue([...path, 'mockVideoImage']) || '';
		const Thumbnail = thumbUrl.match(/(png|jpe?g|svg)$/) ? Misc.ImgThumbnail : Misc.VideoThumbnail;
		title = <>The Video <Thumbnail width={75} height={50} url={thumbUrl} controls={false} /></>;
	}

	const advPath = path.concat('advanced');

	// Tell the user what they're uploading in this spot
	let explanation;
	if (isSocial) {
		if (props.splash) {
			explanation = <p>This is the video which plays on the social platform before the user engages.</p>;
		} else {
			explanation = <p>This is the (optional) video which plays on the Good-Loop unit after the user engages.</p>;
		}
	} else {
		explanation = <p>This is the main advert video which plays in the Good-Loop unit.</p>;
	}

	// Social Design 1 gets "video/image for simulated splash", other types get "image for no-video-yet mock ad"
	const mockUpload = (isSocial && props.splash) ? (
		<PropControl type="bothUpload" path={path} prop="mockSocialImage"
			label={<>Upload an image or video for the splash that shows on the social platform before the viewer swipes through.
				{ }No video? No problem - Use our <a href={'/#video/'+encURI(advert.id)}>Basic Video Maker</a> to create a simple one.
			</>}
			help="As well as uploading a video or image here, you will have to upload it to the social media advertising portal too."
			version="standard"
		/>
	) : (
		<PropControl type="imgUpload" path={path} prop="mockVideoImage" label="Mocking up an ad without a video? Upload a placeholder image here." version="standard" />
	);

	// Warn user of missing videos
	let w = null;
	if (showMainVideos) {
		if (vid0 && vid0.url) {
			if (!(vid0.aspect && vid0.format && vid0.videoSeconds)) w = 'Please provide aspect, format, and length';
		} else if (isSocial) {
			w = 'Does this advert need a post-engage video?';
		} else {
			w = 'Provide at least one video';
		}
	} else if (isSocial && props.splash && !advert.mockSocialImage) {
		w = 'Set a splash video or image to view this advert properly on the demo page';
	}

	/** HACK set default minview: 50% of video, or 100% for short videos.  */
	const defaultMinview = dt => {
		if ( ! dt) return null;
		let minview = dt.n > 10? Math.floor(dt.n / 2) : dt.n;
		return minview;
	};

	return (
		<Card title={title} warning={w} className="videos-editor" {...props}>
			{explanation}
			{showMainVideos ? (
				<Misc.ListEditor path={path.concat('videos')} ItemEditor={EditVideoForm} createText="Add Another Video" />
			) : null}
			<Container><Row>
				<Col>
					{mockUpload}
				</Col>
			</Row></Container>

			<p>
			<small>Video files usually need to be transcoded -- which we do automatically (using the uploader and HandBrake-CLI). 
			If you have a large file (over 20mb), use WeTransfer to send it to Support, who can transcode it for you.</small>
			</p>

			{!isSocial ? (
				<PropControl label="(optional) Time to unlock donation (minview)" type="number" prop="secondsToDonate" path={advPath}
					dflt={defaultMinview(vid0 && vid0.duration)}
					help="How many seconds should pass before the charity chooser unlocks?"
				/>
			) : null}
		</Card>
	);
}

export default EditVideosForm;
